import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/api/endpoints';
import { useUI } from '@/store/ui';

/**
 * Reads tgWebAppStartParam once on mount and reacts to it.  Must be called
 * AFTER auth is confirmed so we don't redirect away before the user is in.
 *
 * Prefixes handled:
 *   ref_<code>      — owned by auth/referral flow; skipped here.
 *   friend_<code>   — POST /friends/connect, toast on result, no redirect.
 *   comment_<id>    — TODO: dedicated comment-focus screen not yet implemented; routing to feed
 *   book_<id>       — /book/<id>
 *   card_<id>       — /feed?focus=<id>
 *   subscription    — /subscription
 *   referrals       — /profile
 *   feed            — /feed
 *   shelf           — /shelf
 */
export function useStartParamRouter(): void {
  const nav = useNavigate();
  const { t } = useTranslation();
  const showToast = useUI(s => s.showToast);

  useEffect(() => {
    const wa = window.Telegram?.WebApp;
    const param = wa?.initDataUnsafe?.start_param as string | undefined;
    if (!param) return;

    if (param.startsWith('ref_')) return;

    if (param.startsWith('friend_')) {
      const code = param.slice('friend_'.length);
      if (!code) return;
      void api.friends
        .connect(code)
        .then(({ data }) => {
          const name = data?.inviter.first_name ?? data?.inviter.username ?? '';
          showToast(t('friends.connected_toast', { name }), 'success');
        })
        .catch(() => showToast(t('friends.connect_failed'), 'error'));
      return;
    }

    if (param.startsWith('comment_')) {
      nav('/feed');
      return;
    }
    if (param.startsWith('book_')) {
      nav(`/book/${encodeURIComponent(param.slice('book_'.length))}`);
      return;
    }
    if (param.startsWith('card_')) {
      // Поддержка составного формата: card_<id>__ref_<refCode>.
      // Привязываем реферала, потом открываем карточку.
      const rest = param.slice('card_'.length);
      const refIdx = rest.indexOf('__ref_');
      const cardId = refIdx >= 0 ? rest.slice(0, refIdx) : rest;
      const refCode = refIdx >= 0 ? rest.slice(refIdx + '__ref_'.length) : '';
      if (refCode) {
        void api.friends
          .connect(refCode)
          .then(({ data }) => {
            const name = data?.inviter.first_name ?? data?.inviter.username ?? '';
            showToast(t('friends.connected_toast', { name }), 'success');
          })
          .catch(() => { /* тихо игнорируем: уже подключён / самого себя / 404 */ });
      }
      nav(`/feed?focus=${encodeURIComponent(cardId)}`);
      return;
    }
    if (param === 'subscription') { nav('/subscription'); return; }
    if (param === 'referrals')    { nav('/profile');      return; }
    if (param === 'feed')         { nav('/feed');         return; }
    if (param === 'shelf')        { nav('/shelf');        return; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
